import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 cursor-pointer",
  {
    variants: {
      variant: {
        default: "bg-white text-black border border-gray-200 hover:bg-black hover:text-white",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        success: "bg-green-600 text-white hover:bg-green-700",
        warning: "bg-yellow-600 text-white hover:bg-yellow-700",
        info: "bg-blue-600 text-white hover:bg-blue-700",
        gradient: "bg-gradient-to-r from-[#f36744] to-[#8e57fb] text-white hover:from-[#e55a3a] hover:to-[#7c4dd8]",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 px-3",
        lg: "h-11 px-8",
        icon: "h-10 w-10",
      },
      radius: {
        default: "rounded-lg",
        sm: "rounded-sm",
        md: "rounded-md",
        lg: "rounded-lg",
        xl: "rounded-xl",
        full: "rounded-full",
        none: "rounded-none",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      radius: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  bgColor?: string
  textColor?: string
  hoverBgColor?: string
  hoverTextColor?: string
  gradient?: string
  hoverGradient?: string
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ 
    className, 
    variant, 
    size, 
    radius, 
    asChild = false, 
    leftIcon, 
    rightIcon, 
    children, 
    bgColor,
    textColor,
    hoverBgColor,
    hoverTextColor,
    gradient,
    hoverGradient,
    style,
    ...props 
  }, ref) => {
    const isIconOnly = size === "icon" 
    // Determine if we should use gradient or solid color
    const hasGradient = gradient && gradient.includes('gradient')
    const hasHoverGradient = hoverGradient && hoverGradient.includes('gradient')
    
    // Build custom styles for colors and gradients
    const customStyles: React.CSSProperties = {
      ...style,
      ...(hasGradient && { background: gradient }),
      ...(!hasGradient && bgColor && { backgroundColor: bgColor }),
      ...(textColor && { color: textColor }),
    }
    
    // Build custom className for hover states
    const customHoverClasses = [
      hasHoverGradient && `hover:bg-[${hoverGradient}]`,
      !hasHoverGradient && hoverBgColor && `hover:bg-[${hoverBgColor}]`,
      hoverTextColor && `hover:text-[${hoverTextColor}]`,
    ].filter(Boolean).join(' ')
    
    const buttonClasses = cn(
      buttonVariants({ variant, size, radius, className }),
      // Only apply custom hover classes if custom colors are provided
      (hoverBgColor || hoverTextColor || hasHoverGradient) && customHoverClasses
    )

    if (asChild) {
      return (
        <Slot
          className={buttonClasses}
          style={customStyles}
          ref={ref}
          {...props}
        >
          {!isIconOnly && leftIcon && (
            <span className="mr-2 flex items-center">
              {leftIcon}
            </span>
          )}
          {isIconOnly && leftIcon && (
            <span className="flex items-center justify-center">
              {leftIcon}
            </span>
          )}
          {!isIconOnly && children}
          {!isIconOnly && rightIcon && (
            <span className="ml-2 flex items-center">
              {rightIcon}
            </span>
          )}
        </Slot>
      )
    }

    return (
      <button
        className={buttonClasses}
        style={customStyles}
        ref={ref}
        {...props}
      >
        {!isIconOnly && leftIcon && (
          <span className="mr-2 flex items-center">
            {leftIcon}
          </span>
        )}
        {isIconOnly && leftIcon && (
          <span className="flex items-center justify-center">
            {leftIcon}
          </span>
        )}
        {!isIconOnly && children}
        {!isIconOnly && rightIcon && (
          <span className="ml-2 flex items-center">
            {rightIcon}
          </span>
        )}
      </button>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants } 